import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Trash2, X } from 'lucide-react';
import { Button } from './ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

/**
 * Reusable floating toolbar for bulk-delete operations on admin tables.
 *
 * Props:
 *   resource: string         — must match a key in backend `BULK_DELETE_WHITELIST`
 *                              (e.g. 'fatture_uscita', 'ddt', 'rapportini').
 *   selectedIds: string[]    — currently selected document ids.
 *   onClear: () => void      — called after a successful delete / restore / cancel.
 *   onDeleted?: () => void   — called after successful delete (e.g. to refetch).
 *   itemLabel?: string       — Italian singular label shown in the toolbar
 *                              (default: "elemento" / plural "elementi").
 */
export function BulkActionToolbar({
  resource,
  selectedIds,
  onClear,
  onDeleted,
  itemLabel = 'elemento',
}) {
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const count = selectedIds.length;

  if (count === 0) return null;

  const plural = count === 1 ? itemLabel : itemLabel + 'i';
  const verb = count === 1 ? 'verrà eliminato' : 'verranno eliminati';

  const handleDelete = async () => {
    setBusy(true);
    try {
      const res = await axios.post(
        `${API}/admin/bulk-delete`,
        { resource, ids: selectedIds },
        { withCredentials: true }
      );
      const n = res.data?.deleted_count ?? 0;
      toast.success(`${n} ${n === 1 ? itemLabel : itemLabel + 'i'} eliminat${n === 1 ? 'o' : 'i'}`);
      setConfirmOpen(false);
      onClear?.();
      onDeleted?.();
    } catch (err) {
      const msg = err?.response?.data?.detail || 'Errore durante eliminazione';
      toast.error(msg);
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <div
        data-testid={`bulk-action-toolbar-${resource}`}
        className="sticky top-0 z-30 mb-3 flex items-center justify-between gap-3 rounded-lg border border-slate-300 bg-slate-900 px-4 py-2.5 text-white shadow-sm"
      >
        <div className="flex items-center gap-3 text-sm">
          <span className="font-medium">
            {count} {plural} selezionat{count === 1 ? 'o' : 'i'}
          </span>
        </div>
        <div className="flex items-center gap-2">
          <Button
            variant="ghost"
            size="sm"
            onClick={onClear}
            className="text-white hover:bg-white/10"
            data-testid={`bulk-clear-${resource}`}
          >
            <X className="h-4 w-4 mr-1" />
            Annulla
          </Button>
          <Button
            variant="destructive"
            size="sm"
            onClick={() => setConfirmOpen(true)}
            className="bg-red-600 hover:bg-red-700"
            data-testid={`bulk-delete-${resource}`}
          >
            <Trash2 className="h-4 w-4 mr-1" />
            Elimina selezionati
          </Button>
        </div>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid={`bulk-confirm-${resource}`}>
          <AlertDialogHeader>
            <AlertDialogTitle>Confermi l'eliminazione?</AlertDialogTitle>
            <AlertDialogDescription>
              {count} {plural} {verb}.<br />
              L'azione è reversibile (soft delete) — gli elementi restano nel database
              e possono essere ripristinati dall'amministratore.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel disabled={busy} data-testid={`bulk-confirm-cancel-${resource}`}>
              Annulla
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              disabled={busy}
              className="bg-red-600 text-white hover:bg-red-700"
              data-testid={`bulk-confirm-ok-${resource}`}
            >
              {busy ? 'Eliminazione…' : `Elimina ${count}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
